export function noop() {}

export function identity(v) {
  return v;
}

/**
 * Creates a memoized function based on the first argument. Any repeated calls to the generated function with the
 * same first argument will return the original value.
 *
 * Return values are stored in a Map, so they will persist as long as there's a reference to the returned function
 * in memory.
 *
 * @param fn
 * @returns {function(...[*]): any}
 */
export function memo(fn) {
  const memory = new Map();
  const memoFn = function (key, ...args) {
    if (!memory.has(key)) {
      memory.set(key, fn(key, ...args));
    }
    return memory.get(key);
  };
  memoFn.delete = (key) => memory.delete(key);
  memoFn.clear = () => memory.clear();
  return memoFn;
}

/**
 * Creates a memoized function based on a mapped value from the arguments. Any repeated calls to the generated
 * that lead to the same key will return the original value.
 *
 * This method takes two methods, one to translate the arguments to a key, and a second that processes the arguments
 * if we have not already processed that key.
 *
 * The second argument will be provided with an additional parameter, the key, which will be the first argument to
 * the function.
 *
 * Return values are stored in a Map, so they will persist as long as there's a reference to the returned function
 * in memory.
 *
 * @param {function(...[*])} mapper - Method to map the arguments to a key
 * @param {function(*, ...[*])} fn
 * @returns {function(...[*]): any}
 */
export function mappedMemo(mapper, fn) {
  const memory = new Map();
  const memoFn = function (...args) {
    const key = mapper(...args);
    if (!memory.has(key)) {
      memory.set(key, fn(key, ...args));
    }
    return memory.get(key);
  };
  memoFn.delete = (...args) => memory.delete(mapper(...args));
  memoFn.clear = () => {
    memory.clear();
  };
  return memoFn;
}
